import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import SessionIcon from '../../assets/icons/session.svg?react';
import nextVCImage from '../../assets/images/vc-fonendoscopio.svg';
import { Logo } from './style';
import {
	NextVideoConsultationImage,
	NextVideoConsultationSubtitle,
	NextVideoConsultationTitle,
	WaitingRoomBottom,
	WaitingRoomCard,
	WaitingRoomCardContent,
	WaitingRoomCardHeader,
	WaitingRoomContainer,
	WaitingRoomContent,
	WaitingRoomLogo,
	WaitingRoomMeetControlsContainer,
	WaitingRoomTitle,
} from './style';
import { useWaitingRoom } from './useWaitingRoom';
import InvalidCodePage from '../invalid-code/InvalidCodePage';
import TooEarlyPage from '../too-early/TooEarlyPage';
import UnavailableMeetPage from '../unavailable-meet/UnavailableMeetPage';
import AlreadyFinishedMeetPage from '../already-finished-meet/AlreadyFinishedMeetPage';
import {
	useTranslationsContext
} from '../../../features/Translations/providers/TranslationsContextProvider';
import { useMeetContext } from '../../../features/Meet';
import { PermissionsPopUp } from '../../../features/Media/components/PermissionsPopUp';
import {
	MeetTimeRemainingModal
} from '../../../features/MeetTimeRemaining/MeetTimeRemainingModal/MeetTimeRemainingModal';
import {
	ProfessionalNotAvailableModal
} from '../../../features/MeetProfessionalAvailability/components/ProfessionalNotAvailableModal';
import { useIsMobile } from '../../hooks/useIsMobile';
import { SheetModal } from '../../components/SheetModal/SheetModal';
import { Column } from '../../components/Column/Column';
import { VideoPreview } from '../../components/VideoPreview/VideoPreview';
import { ParticipantInfo } from '../../../features/ParticipantInfo/ParticipantInfo';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { Row } from '../../components/Row/Row';
import { CamControl } from '../../components/MeetControl/CamControl';
import { Button } from '../../components/Button/Button';
import { AudioControl } from '../../components/MeetControl/AudioControl';
import { Space } from '../../components/Space/style';
import { SheetModalHeader } from '../../components/SheetModal/SheetModalHeader';
import { useMediaContext } from '../../../features/Media/providers/MediaContextProvider';
import { CapabilitiesKoModal } from '../../../features/VideoCall/components/CapabilitiesKoModal';
import {
	useVideoCallContext
} from '../../../features/VideoCall/providers/VideoCallContextProvider';
import {
	ProfessionalAvailableModal
} from '../../../features/MeetProfessionalAvailability/components/ProfessionalAvailableModal';
import { DevicesInUseModal } from './components/DevicesInUseModal.tsx';

export interface WaitingRoomProps {
	code?: string;
	tenant?: string;
}

export function WaitingRoom({ code, tenant }: Readonly<WaitingRoomProps>) {
	const theme = useTheme();
	const { t } = useTranslationsContext();
	const { meet, retrieveMeetError, isProfessional, specialty } = useMeetContext();
	const { capabilitiesAlert, toggleCapabilitiesAlert } = useVideoCallContext();
	const {
		permissionsModalOpened,
		acceptedMediaPermissions,
		devicesInUse,
		retryDeviceAccess,
	} = useMediaContext();

	const {
		cannotAccess,
		infoOpened,
		notStarted,
		startDate,
		headerHeight,
		cardOffsetTop,
		bottomCardHeight,
		remainingModal,
		professionalNotAvailableModal,
		hideOptionDisabled,
		muteOptionDisabled,
		hiddenCam,
		muted,
		professionalAvailableModal,
		toggleProfessionalAvailableModal,
		toggleCamVisibility,
		toggleMic,
		onTimerFinished,
		closeRemainingModal,
		toggleInfo,
		access,
		headerRefChanged,
		onCardRefChanged,
		onBottomCardRefChanged,
		onPermissionsModalClose,
		toggleProfessionalNotAvailableModal,
	} = useWaitingRoom(code, tenant);

	const isMobile = useIsMobile();
	const isDesktop = useIsDesktop();

	const retryOnError = useCallback(() => {
		location.reload();
	}, []);


	const isNotPermissions = useMemo(() => {
		return permissionsModalOpened;
	}, [permissionsModalOpened]);

	const isCapabilitiesKoModal = useMemo(() => {
		return !isNotPermissions && capabilitiesAlert;
	}, [isNotPermissions, capabilitiesAlert]);

	const isDevicesInUseModal = useMemo(() => {
		return !isNotPermissions && devicesInUse;
	}, [isNotPermissions, devicesInUse]);

	const isTimeRemainingModal = useMemo(() => {
		return !isNotPermissions && !professionalAvailableModal && !professionalNotAvailableModal && !isCapabilitiesKoModal && !isDevicesInUseModal && notStarted;
	}, [isNotPermissions, professionalAvailableModal, professionalNotAvailableModal, isCapabilitiesKoModal, isDevicesInUseModal, notStarted]);

	const isProfessionalNotAvailableModal = useMemo(() => {
		return !isNotPermissions && !isTimeRemainingModal && !professionalAvailableModal && !isCapabilitiesKoModal && !isDevicesInUseModal && professionalNotAvailableModal;
	}, [isNotPermissions, isTimeRemainingModal, professionalAvailableModal, isCapabilitiesKoModal, isDevicesInUseModal, professionalNotAvailableModal]);

	const isProfessionalAvailableModal = useMemo(() => {
		return !isNotPermissions && !isTimeRemainingModal && !professionalNotAvailableModal && !isCapabilitiesKoModal && !isDevicesInUseModal && professionalAvailableModal;
	}, [isNotPermissions, isTimeRemainingModal, professionalNotAvailableModal, isCapabilitiesKoModal, isDevicesInUseModal, professionalAvailableModal]);

	return (
		<>
			{retrieveMeetError || !code ? (
				<InvalidCodePage
					message={retrieveMeetError}
					onRetry={retryOnError}
				/>
			) : (
				(() => {
					switch (meet?.status) {
					case 'CONFIRMED':
						return (
							<TooEarlyPage
								onRetry={retryOnError}
							/>
						);
					case 'NOT_AVAILABLE':
						return (
							<UnavailableMeetPage/>
						);
					case 'FINISHED':
						return (
							<AlreadyFinishedMeetPage/>
						);
					default:
						return (
							<WaitingRoomContainer>
								{meet && <>
									{isNotPermissions && (
										<PermissionsPopUp
											show={permissionsModalOpened}
											onClose={onPermissionsModalClose}
										/>
									)}

									{isTimeRemainingModal && (
										<MeetTimeRemainingModal
											show={remainingModal}
											onTimerFinished={onTimerFinished}
											onClick={access}
											onClose={closeRemainingModal}
										/>
									)}

									{isProfessionalNotAvailableModal && (
										<ProfessionalNotAvailableModal meet={meet}
																		   onClose={() => toggleProfessionalNotAvailableModal(false)}/>
									)}

									{isProfessionalAvailableModal && (
										<ProfessionalAvailableModal onClick={() => {
											toggleProfessionalAvailableModal(false);
											access();
										}}
										onClose={() => toggleProfessionalAvailableModal(false)}/>
									)}

									{isCapabilitiesKoModal && (
										<CapabilitiesKoModal
											onClose={() => toggleCapabilitiesAlert(false)}/>
									)}

									{isDevicesInUseModal && (
										<DevicesInUseModal show={devicesInUse}
															   onClickBtn={retryDeviceAccess}/>)}

									{isMobile && (
										<SheetModal
											data-testid="waiting-room_info"
											isOpen={infoOpened}
											header={
												<SheetModalHeader
													prepend={
														<SessionIcon
															fill={theme.colors.brand.primary[300]}
															width={25}
															height={25}
														/>
													}
													title={t('waiting_room.info')}
												/>
											}
											onClose={toggleInfo}
										/>
									)}

									<WaitingRoomLogo $top={cardOffsetTop / 2}
														 ref={headerRefChanged}>
										<Logo src={theme.logo}/>
									</WaitingRoomLogo>
									<WaitingRoomContent $offsetTop={headerHeight}
										$offsetBottom={bottomCardHeight}>
										<WaitingRoomCard ref={onCardRefChanged}>
											<WaitingRoomCardHeader>
												<NextVideoConsultationTitle>
													{isProfessional
														? t('waiting_room.next_videoconsultation')
														: t('waiting_room.videoconsultation')}
												</NextVideoConsultationTitle>
												<NextVideoConsultationSubtitle>
													<Column $hcenter={!isDesktop}>
														<span>{specialty}</span>
														<span>
                              									{startDate}
                            								</span>
													</Column>
												</NextVideoConsultationSubtitle>
												{isDesktop && <NextVideoConsultationImage
													src={nextVCImage}/>}
											</WaitingRoomCardHeader>
											<WaitingRoomCardContent>
												<Column $gap={16} $hcenter>
													<VideoPreview
														$showVideo={!hideOptionDisabled}
														grantedPermissions={acceptedMediaPermissions}
													/>
												</Column>
												<Column $gap={16} $spaceBetween $fillHeight>
													<Column $gap={28} $hcenter
														$vcenter={!isMobile}
														$fillHeight={!isMobile}>
														<WaitingRoomTitle>{t('waiting_room.title')}</WaitingRoomTitle>
														<ParticipantInfo
															$hcenter
															$boldColor={theme.colors.brand.text}
															$textColor={theme.colors.brand.text}
														/>
													</Column>
													{!isMobile && (
														<Column $hcenter>
															<Space size={32}
																	   direction={'vertical'}/>
															<WaitingRoomMeetControlsContainer>
																<Row $hcenter $fillSpace
																		 $gap={18}>
																	<AudioControl
																		$off={muteOptionDisabled}
																		disabled={!acceptedMediaPermissions}
																		$withMenu
																		vMenuPadding={16}
																		onClick={toggleMic}
																	/>
																	<CamControl
																		$off={hideOptionDisabled}
																		disabled={!acceptedMediaPermissions}
																		$withMenu
																		vMenuPadding={16}
																		onClick={toggleCamVisibility}
																	/>
																	<Button
																		data-test="waiting-room_access-cta"
																		onClick={access}
																		$color={'primary'}
																		$block
																		$rounded
																		disabled={cannotAccess}
																	>
																		{t('waiting_room.access')}
																	</Button>
																</Row>
															</WaitingRoomMeetControlsContainer>
														</Column>
													)}
												</Column>
											</WaitingRoomCardContent>
										</WaitingRoomCard>
									</WaitingRoomContent>

									{isMobile && (
										<WaitingRoomBottom ref={onBottomCardRefChanged}>
											<Row $hcenter $fillSpace $gap={18}>
												<AudioControl
													$off={muted}
													disabled={!acceptedMediaPermissions}
													onClick={toggleMic}
													$withMenu
												/>
												<CamControl
													$off={hiddenCam}
													disabled={!acceptedMediaPermissions}
													onClick={toggleCamVisibility}
												/>
												<Button
													onClick={access}
													$color={'primary'}
													$block
													$rounded
													disabled={cannotAccess}
												>
													{t('waiting_room.access')}
												</Button>
											</Row>
										</WaitingRoomBottom>
									)}
								</>}
							</WaitingRoomContainer>);
					}
				})()
			)}
		</>
	);
}
